/**
 * What the compute gate would answer a submission for `product`, read from
 * `GET /subscription` (the account page's read) where that read can be sure
 * (ENG-3091).
 *
 * The 403 a submission gets is the authority, and every surface that submits
 * shows it (`entitlementDenial`). This exists for the two places that need the
 * answer without a submission in hand: after a reload, when the refused
 * diagnose POST is gone and the geometry just sits idle with no diagnosis;
 * and MeshGen's top bar, which says up front that MeshGen is not available in
 * this workspace.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { handleGetSubscriptionSubscriptionGetOptions } from '../api/generated/@tanstack/react-query.gen';
import type { SubscriptionResponse } from '../api/generated/types.gen';
import type { EntitlementDenial } from '@/lib/entitlementDenial';

/**
 * Mirrors `require_seat` then `require_product_access`
 * (`backend/src/job/billing/entitlements.py`) only as far as an org-level
 * read allows. No grant for `product` → `product_not_entitled`. A grant with
 * a seat capacity and nobody seated in it → `seat_not_assigned`, since no
 * member can hold one. Whether THIS member holds one of the assigned seats is
 * not knowable here (the member list is owner-only), so that case is `null`,
 * as is an unbounded grant (`seats: null`). Assumes the gate is on, as it is
 * in every deployed environment (`infra/config/environments.ts`).
 */
export function subscriptionRefusal(
  subscription: SubscriptionResponse | undefined,
  product: string,
): EntitlementDenial | null {
  if (!subscription) return null;
  const grant = subscription.grants.find((g) => g.product === product);
  if (!grant) return { reason: 'subscription', product };
  const assigned = subscription.seats.by_product[product]?.assigned ?? 0;
  if (grant.seats != null && assigned === 0) {
    return { reason: 'seat', product };
  }
  return null;
}

/** {@link subscriptionRefusal} over the shared query, fetched only while `enabled`. */
export function useSubscriptionRefusal(
  product: string,
  enabled: boolean,
): EntitlementDenial | null {
  const { data } = useQuery({
    ...handleGetSubscriptionSubscriptionGetOptions(),
    enabled,
    // Every mount on the page asks; one read a minute is plenty for a plan
    // that changes at checkout.
    staleTime: 60_000,
  });
  return useMemo(
    () => (enabled ? subscriptionRefusal(data, product) : null),
    [enabled, data, product],
  );
}
